import { Link, useParams } from 'react-router-dom'
import DocLayout from '../components/docs/DocLayout'
import CopyButton from '../components/docs/CopyButton'
import Icon from '../components/docs/Icon'
import Specimen from '../components/library/Specimens'
import { componentReferences } from '../components/library/specimen-groups'
import NotFoundPage from './NotFoundPage'
import { componentCategories, components, findComponent } from '@/content/components'
import { usePrefs } from '@/state/prefs'
import { useCustomRoles } from '@/state/useCustomRoles'
import Cased from '@/components/ui/Cased'
import Pager from '../components/docs/Pager'

export default function ComponentDetailPage() {
  const { slug } = useParams()
  const { prefs } = usePrefs()
  const customRoles = useCustomRoles()
  const component = findComponent(slug)
  if (!component) return <NotFoundPage />

  const reference = componentReferences[component.id]
  // The catalog's order, by category, so Previous and Next walk the whole library.
  const ordered = componentCategories.flatMap((category) => components.filter((item) => item.category === category))
  const index = ordered.findIndex((item) => item.id === component.id)
  const previous = index > 0 ? ordered[index - 1] : undefined
  const next = index < ordered.length - 1 ? ordered[index + 1] : undefined

  return (
    <DocLayout section={{ label: 'Components', to: '/components' }}
      eyebrow="Components"
      title={component.name}
      wide
      intro={<p>{component.summary}</p>}
    >
      <section className="doc-section lib-detail__live" aria-labelledby="live-title">
        <h2 id="live-title"><Cased>Live specimen</Cased></h2>
        <div className="lib-specimen">
          <Specimen id={component.id} />
        </div>
        <p className="mds-cluster lib-detail__files">
          <a className="mds-button mds-button--secondary mds-button--sm" href={`/mds/design/svg/components/${component.id}.svg`} download>
            <Icon name="download" size={16} />
            SVG
          </a>
          <span className="mds-hint">
            Editable layers for Figma, Illustrator and Canva. The <Link to="/design-tools">Figma plugin</Link> draws it with its
            colors bound to variables.
          </span>
        </p>
      </section>

      {reference && (
        <section className="doc-section" aria-labelledby="reference-title">
          <h2 id="reference-title"><Cased>{reference.title}</Cased></h2>
          <p>{reference.intro}</p>
          <reference.Body />
        </section>
      )}

      <section className="doc-section" aria-labelledby="modes-title">
        <h2 id="modes-title"><Cased>Light and dark</Cased></h2>
        <p>Both themes render from the same markup. The surrounding mission theme supplies the roles.</p>
        <div className="lib-modes">
          {(['light', 'dark'] as const).map((mode) => (
            <div key={mode} className="lib-mode" data-theme={mode} data-mission={prefs.mission} style={customRoles(mode)}>
              <span className="mds-eyebrow">{mode}</span>
              <div inert aria-hidden="true">
                <Specimen id={component.id} mini />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="doc-section" aria-labelledby="use-title">
        <h2 id="use-title"><Cased>When to use it</Cased></h2>
        <ul>
          {component.useWhen.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="doc-section" aria-labelledby="code-title">
        <h2 id="code-title"><Cased>Markup</Cased></h2>
        <pre className="mds-code" tabIndex={0}>
          <code>{component.example}</code>
        </pre>
        <CopyButton text={component.example} label="Copy markup" />
      </section>

      <div className="lib-detail__columns">
        <section className="doc-section" aria-labelledby="anatomy-title">
          <h2 id="anatomy-title"><Cased>Anatomy</Cased></h2>
          <ol className="lib-anatomy">
            {component.anatomy.map((part) => (
              <li key={part}>{part}</li>
            ))}
          </ol>
        </section>
        <section className="doc-section" aria-labelledby="a11y-title">
          <h2 id="a11y-title"><Cased>Accessibility</Cased></h2>
          <ul>
            {component.accessibility.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </section>
      </div>

      <section className="doc-section" aria-labelledby="migrate-title">
        <h2 id="migrate-title"><Cased>Migrating from USWDS</Cased></h2>
        {component.uswds.length ? (
          <div className="doc-table-wrap" tabIndex={0}>
            <table className="doc-table">
              <caption className="mds-visually-hidden">USWDS components replaced by {component.name}</caption>
              <thead>
                <tr>
                  <th scope="col">USWDS component</th>
                  <th scope="col">MDS replacement</th>
                </tr>
              </thead>
              <tbody>
                {component.uswds.map((name) => (
                  <tr key={name}>
                    <td>
                      <a
                        href={`https://designsystem.digital.gov/components/${name.toLowerCase().replace(' or ', ' ').replace(/\s+/g, '-')}/`}
                      >
                        {name}
                      </a>
                    </td>
                    <td>{component.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>USWDS has no equivalent; this component is new in MDS.</p>
        )}
      </section>

      <section className="doc-section" aria-labelledby="tokens-title">
        <h2 id="tokens-title"><Cased>Classes and tokens</Cased></h2>
        <div className="lib-tokens">
          <div>
            <h3><Cased>Classes</Cased></h3>
            <ul className="lib-chips">
              {component.classes.map((name) => (
                <li key={name}>
                  <code>.{name}</code>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3><Cased>Tokens</Cased></h3>
            <ul className="lib-chips">
              {component.tokens.map((name) => (
                <li key={name}>
                  <code>{name}</code>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <Pager
        label="Components"
        previous={previous && { to: `/components/${previous.id}`, title: previous.name }}
        next={next && { to: `/components/${next.id}`, title: next.name }}
      />
    </DocLayout>
  )
}
